import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AuthContext } from '../../context/AuthContext';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';

const emptyForm = { title: '', category: 'OTHER', visibility: 'PRIVATE', expiresAt: '' };

export default function Documents() {
  const { session } = useContext(AuthContext);
  const admin = session.roles.includes('ADMIN') || session.roles.includes('SUPER_ADMIN');
  const { get, post, delete: remove } = useApi();
  const [items, setItems] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const file = useRef();

  const load = useCallback(async () => {
    try { setItems(await get('/api/documents')); setError(''); }
    catch (requestError) { setError(requestError.message); }
  }, [get]);

  useEffect(() => { load(); get('/api/access-control/me').then(profile => setCanCreate(!profile.customRolesAssigned || profile.permissions?.some(permission => ['DOCUMENTS:CREATE','DOCUMENTS:MANAGE','*:MANAGE'].includes(permission)))).catch(() => setCanCreate(false)); }, [load, get]);

  const upload = async event => {
    event.preventDefault();
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => { if (value) body.append(key, value); });
    body.append('file', file.current.files[0]);
    try {
      await post('/api/documents', body);
      event.target.reset();
      setForm(emptyForm);
      await load();
    } catch (requestError) { setError(requestError.message); }
  };

  const download = async document => {
    try {
      const response = await api.get(`/api/documents/${document.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.originalName; anchor.click(); URL.revokeObjectURL(url);
    } catch (requestError) { setError(requestError.message); }
  };

  const deleteDocument = async id => {
    try { await remove(`/api/documents/${id}`); await load(); }
    catch (requestError) { setError(requestError.message); }
  };

  return <div className="engagement-page">
    <div className="engagement-heading"><div><span>Secure vault</span><h2>Resident &amp; community documents</h2><p>Private records remain protected; administrator-published guidelines and policies can be shared community-wide.</p></div></div>
    {error && <Alert type="danger" message={error} />}
    <div className="engagement-layout">
      {canCreate ? <form className="engagement-form" onSubmit={upload}>
        <h3>Upload document</h3>
        <label>Title<input required className="form-control" value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} /></label>
        <label>Category<select className="form-select" value={form.category} onChange={event => setForm({ ...form, category: event.target.value })}>{['IDENTITY', 'VEHICLE', 'TENANCY', 'INSURANCE', 'GUIDELINE', 'POLICY', 'COMMUNITY', 'OTHER'].map(category => <option key={category}>{category}</option>)}</select></label>
        <label>Visibility<select className="form-select" value={form.visibility} onChange={event => setForm({ ...form, visibility: event.target.value })}><option value="PRIVATE">Private — only owner</option><option value="COMMUNITY_ADMIN">Owner and community admin</option>{admin && <option value="COMMUNITY">Community — all residents</option>}</select></label>
        <label>Expiry date <span className="text-muted">(optional)</span><input type="date" className="form-control" value={form.expiresAt} onChange={event => setForm({ ...form, expiresAt: event.target.value })} /><small className="text-muted">Leave blank for infinite validity.</small></label>
        <label>PDF or image<input required ref={file} type="file" accept=".pdf,.jpg,.jpeg,.png" className="form-control" /></label>
        <button className="btn btn-primary">Upload securely</button>
      </form> : <div className="engagement-form"><h3>Document library</h3><Alert type="info" message="Your assigned role provides read-only document access. Contact your community administrator if you need upload permission." /></div>}
      <section className="engagement-list"><header><h3>Available documents</h3></header>
        {items.length === 0 ? <div className="engagement-empty">No documents uploaded.</div> : items.map(document => <article className="engagement-item" key={document.id}>
          <DocumentPreview document={document} />
          <div className="engagement-item-top"><span>{document.category}</span><span>{(document.sizeBytes / 1024).toFixed(0)} KB</span></div>
          <h4>{document.title}</h4><p>{document.originalName}</p>
          <small>Uploaded {new Date(document.createdAt).toLocaleString()} by {document.uploadedByName}</small>
          <small className="d-block">Visibility: {document.visibility.replaceAll('_', ' ')}</small>
          <small className="d-block">Expiry: {document.expiresAt ? new Date(`${document.expiresAt}T00:00:00`).toLocaleDateString() : 'No expiry'}</small>
          <div className="engagement-actions"><button onClick={() => download(document)}>Download</button>{document.manageable && <button onClick={() => deleteDocument(document.id)}>Delete</button>}</div>
        </article>)}
      </section>
    </div>
  </div>;
}

function DocumentPreview({ document }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let active = true; let objectUrl = '';
    api.get(`/api/documents/${document.id}/preview`, { responseType: 'blob' }).then(response => {
      if (active) { objectUrl = URL.createObjectURL(response.data); setUrl(objectUrl); }
    }).catch(() => {});
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [document.id]);
  if (!url) return <div className="document-thumb document-thumb-loading">Loading preview...</div>;
  return document.contentType === 'application/pdf'
    ? <iframe className="document-thumb" src={`${url}#toolbar=0&navpanes=0`} title={`${document.title} preview`} />
    : <img className="document-thumb" src={url} alt={`${document.title} preview`} />;
}
